// src/utils/viewState.js

export const DEFAULT_VIEW = Object.freeze({
  // Navigation
  tab: "occupancy", // "occupancy" | "flow"

  // Shared basic controls
  areaFilterId: "all",
  timeWindowMins: 120,

  // Flow tab only
  metric: "ingress",    // "ingress" | "egress" | "net"
  grouping: "subgroup", // "group" | "subgroup" | "camera"
  viewMode: "compare",  // "single" | "compare"
  flowMode: "flow",     // "flow" | "cumulative"

  // Compare-only presentation options
  stacked: true,        // true = stacked, false = overlap
  showTotal: true       // show total line in compare view
});

export function normalizeViewState(input = {}) {
  const s = {
    ...DEFAULT_VIEW,
    ...input,

    // 🔒 tab is user-controlled; never auto-switch
    tab: input.tab ?? DEFAULT_VIEW.tab
  };

  // --- sanitize numbers ---
  s.timeWindowMins = clampInt(s.timeWindowMins, 5, 24 * 60, DEFAULT_VIEW.timeWindowMins);

  // --- sanitize enums ---
  s.tab = oneOf(s.tab, ["occupancy", "flow"], DEFAULT_VIEW.tab);
  s.metric = oneOf(s.metric, ["ingress", "egress", "net"], DEFAULT_VIEW.metric);
  s.grouping = oneOf(s.grouping, ["group", "subgroup", "camera"], DEFAULT_VIEW.grouping);
  s.viewMode = oneOf(s.viewMode, ["single", "compare"], DEFAULT_VIEW.viewMode);
  s.flowMode = oneOf(s.flowMode, ["flow", "cumulative"], DEFAULT_VIEW.flowMode);

  s.stacked = !!s.stacked;
  s.showTotal = !!s.showTotal;

  // --- Tab-specific guardrails ---
  if (s.tab === "flow") {
    // View-mode guardrails:
    // - In SINGLE view: stacked/overlap doesn't apply, total-line doesn't apply in this UI.
    //   We keep the values around (so when you flip back to compare, your last choice returns),
    //   but we force deterministic defaults for the renderer.
    //
    // Recommended renderer behaviour:
    // - Single view: stacked=false, showTotal=true (so you still get a useful "Total" line)
    if (s.viewMode === "single") {
      // Keep user's compare prefs remembered in separate keys? (not needed now)
      // Force canonical single-view rendering defaults:
      s.stacked = false;
      s.showTotal = true;
    }

    // - In COMPARE view: both stacked and total line are meaningful, keep user choices.
    if (s.viewMode === "compare") {
      // No change; user's s.stacked and s.showTotal are respected.
    }
  }

  // Occupancy tab deliberately ignores flow settings (but we keep them so switching back is seamless)
  // No changes needed.

  return s;
}

// URL serialization (unchanged from your tab-based version)
export function stateToSearch(state) {
  const s = normalizeViewState(state);
  const p = new URLSearchParams();

  p.set("tab", s.tab);
  p.set("f", s.areaFilterId);
  p.set("t", String(s.timeWindowMins));

  if (s.tab === "flow") {
    p.set("m", s.metric);
    p.set("g", s.grouping);
    p.set("v", s.viewMode);
    p.set("fm", s.flowMode);

    // Always include these so URLs are stable even when switching view modes
    p.set("s", s.stacked ? "1" : "0");
    p.set("tot", s.showTotal ? "1" : "0");
  }

  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export function searchToState(search = "") {
  const p = new URLSearchParams(search.replace(/^\?/, ""));

  const s = {
    ...DEFAULT_VIEW,

    tab: p.get("tab") ?? DEFAULT_VIEW.tab,
    areaFilterId: p.get("f") ?? DEFAULT_VIEW.areaFilterId,
    timeWindowMins: parseInt(p.get("t") ?? `${DEFAULT_VIEW.timeWindowMins}`, 10),

    metric: p.get("m") ?? DEFAULT_VIEW.metric,
    grouping: p.get("g") ?? DEFAULT_VIEW.grouping,
    viewMode: p.get("v") ?? DEFAULT_VIEW.viewMode,
    flowMode: p.get("fm") ?? DEFAULT_VIEW.flowMode,

    stacked: (p.get("s") ?? (DEFAULT_VIEW.stacked ? "1" : "0")) === "1",
    showTotal: (p.get("tot") ?? (DEFAULT_VIEW.showTotal ? "1" : "0")) === "1"
  };

  return normalizeViewState(s);
}

// helpers
function oneOf(v, allowed, fallback) {
  return allowed.includes(v) ? v : fallback;
}

function clampInt(v, min, max, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(n)));
}
